import ComparePage, { CompareRow, CompareFaq, Reason } from '@comp/compare/compare-template'
import AssetDownload from '@comp/product/asset-download'

interface PageContent {
  seoTitle: string
  seoDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  reasons: Reason[]
  tableTitle: [string, string]
  tableBody: string
  rows: CompareRow[]
  faqs: CompareFaq[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    seoTitle: 'Teleport Alternative: Monofor | Engineering access plus full PAM',
    seoDescription:
      'Looking for a Teleport alternative? Monopam covers the SSH, Kubernetes, and database access engineers expect, plus the credential vault, session video, approvals, and Windows depth enterprises require.',
    heroEyebrow: 'Teleport alternative',
    heroTitle: ['Access engineers love.', 'PAM auditors expect.'],
    heroBody:
      'Teleport modernized infrastructure access with short-lived certificates for SSH, Kubernetes, and databases, and earned its following. But its center of gravity is engineering access. Enterprises also need the classic PAM spine: vaulted credentials for accounts that must exist, session video, approval workflows, and real Windows depth. Monopam delivers both sides in one platform, self-hosted or cloud, deployed in minutes.',
    reasons: [
      {
        title: 'The accounts that must exist',
        body: 'Short-lived certificates are the right model where targets support them. But root, sa, service accounts, and appliance logins do not go away; they need vaulting, rotation, and check-out control. Monopam runs both models side by side: ephemeral access where possible, vaulted credentials where reality demands them.',
      },
      {
        title: 'Windows is not an afterthought',
        body: 'Enterprise privileged access is heavily Windows: RDP to servers, domain admin sessions, appliances that only speak VNC or Telnet. Monopam treats browser-based RDP as first-class, with video and keystroke-transcript recording, alongside SSH, VNC, and Telnet in the same console.',
      },
      {
        title: 'One platform past the infrastructure',
        body: 'Infrastructure access is one slice of privileged access. Monopam shares a platform with Monosign (IAM) and Monosync (IGA), so SSO, MFA, lifecycle, and access reviews live in the same console, priced per concurrent session, not per resource.',
      },
    ],
    tableTitle: ['Monofor vs Teleport,', 'feature by feature.'],
    tableBody:
      'On the modern infrastructure surface (SSH, Kubernetes, databases), the two are at parity. The gap opens on the classic PAM side: vaulting, Windows depth, and how far the platform reaches beyond engineering.',
    rows: [
      {
        feature: 'SSH access with recording',
        detail: 'Native clients or browser, full session capture',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Kubernetes & OpenShift access',
        detail: 'Native kubectl / oc, verb & namespace policy, JIT tokens',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Database access with native tools',
        detail: 'psql, mysql, SSMS and friends through the gateway',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'SQL command policy & dynamic data masking',
        detail: 'Statement-level control, masked result sets',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Browser-based RDP / VNC / Telnet',
        detail: 'First-class Windows and appliance access',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Credential vault with rotation',
        detail: 'For the accounts that must exist',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Session video + keystroke transcript',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Just-in-time access with approvals',
        detail: 'Time-bound, approval-gated grants',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'IAM + IGA in the same platform',
        detail: 'SSO, MFA, lifecycle, access reviews',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Self-hosted or cloud',
        detail: 'Same product, same price',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Per-concurrent-session pricing',
        detail: 'Not per resource or per user',
        monofor: true,
        competitor: 'Quote-based',
      },
      {
        feature: 'Deploy in minutes without agents on every target',
        monofor: true,
        competitor: 'partial',
      },
    ],
    faqs: [
      {
        q: 'Does Monopam cover Kubernetes the way Teleport does?',
        a: 'Yes. Monopam brokers Kubernetes and OpenShift access with short-lived kubeconfigs and just-in-time TokenRequest tokens, enforces verb- and namespace-level policy, records exec sessions, and lets engineers keep using native kubectl and oc.',
      },
      {
        q: 'How deep is the Windows / RDP support?',
        a: 'First-class. RDP runs in the browser with no client installs, sessions are recorded as video plus keystroke transcript, and vaulted credentials are injected so admins never see the password. VNC and Telnet cover the appliances that never modernized.',
      },
      {
        q: 'Do engineers have to give up their tools?',
        a: 'No. kubectl, oc, psql, mysql, and native SSH clients all work through the Monopam gateway with policy and recording applied in the path. The browser console is an option, not a requirement.',
      },
      {
        q: 'If short-lived certificates are the future, why vault credentials at all?',
        a: 'Because many privileged accounts must exist regardless: root and local admin, database sa, service accounts, and logins on appliances that will never support certificate auth. Those need vaulting, rotation, and check-out control: the part of PAM that certificate-only models leave to you.',
      },
      {
        q: 'Can we run it self-hosted?',
        a: 'Yes. Monopam deploys self-hosted on your infrastructure or as SaaS (same product, same price) and is priced per concurrent session rather than per resource, so growing your fleet does not grow your bill.',
      },
    ],
  },
  tr: {
    seoTitle: 'Teleport Alternatifi: Monofor | Mühendis erişimi artı tam PAM',
    seoDescription:
      'Teleport alternatifi mi arıyorsunuz? Monopam; mühendislerin beklediği SSH, Kubernetes ve veritabanı erişimini, artı kurumların gerektirdiği kimlik bilgisi kasasını, oturum videosunu, onayları ve Windows derinliğini sunar.',
    heroEyebrow: 'Teleport alternatifi',
    heroTitle: ['Mühendislerin sevdiği erişim.', 'Denetçilerin beklediği PAM.'],
    heroBody:
      'Teleport; SSH, Kubernetes ve veritabanları için kısa ömürlü sertifikalarla altyapı erişimini modernleştirdi ve hayran kitlesini hak etti. Ancak ağırlık merkezi mühendislik erişimi. Kurumların klasik PAM omurgasına da ihtiyacı var: var olmak zorunda olan hesaplar için kasa, oturum videosu, onay akışları ve gerçek Windows derinliği. Monopam iki tarafı tek platformda sunar; kendi altyapınızda veya bulutta, dakikalar içinde kurulur.',
    reasons: [
      {
        title: 'Var olmak zorunda olan hesaplar',
        body: 'Kısa ömürlü sertifikalar, hedeflerin desteklediği yerde doğru modeldir. Ama root, sa, servis hesapları ve cihaz girişleri ortadan kalkmaz; kasa, rotasyon ve teslim alma kontrolü ister. Monopam iki modeli yan yana çalıştırır: mümkün olduğunda geçici erişim, gerçekliğin dayattığı yerde kasadaki kimlik bilgileri.',
      },
      {
        title: 'Windows sonradan akla gelen bir ek değil',
        body: 'Kurumsal ayrıcalıklı erişim ağırlıklı olarak Windows’tur: sunuculara RDP, domain admin oturumları, yalnızca VNC veya Telnet konuşan cihazlar. Monopam tarayıcı tabanlı RDP’yi birinci sınıf ele alır (video ve tuş vuruşu dökümü kaydıyla), aynı konsolda SSH, VNC ve Telnet ile birlikte.',
      },
      {
        title: 'Altyapının ötesinde tek platform',
        body: 'Altyapı erişimi, ayrıcalıklı erişimin yalnızca bir dilimidir. Monopam; Monosign (IAM) ve Monosync (IGA) ile aynı platformu paylaşır: SSO, MFA, yaşam döngüsü ve erişim gözden geçirmeleri aynı konsolda yaşar; kaynak başına değil, eşzamanlı oturum başına fiyatlandırılır.',
      },
    ],
    tableTitle: ['Monofor ve Teleport,', 'özellik özellik.'],
    tableBody:
      'Modern altyapı yüzeyinde (SSH, Kubernetes, veritabanları) ikisi başa baş. Fark klasik PAM tarafında açılıyor: kasa, Windows derinliği ve platformun mühendisliğin ötesine ne kadar uzandığı.',
    rows: [
      {
        feature: 'Kayıtlı SSH erişimi',
        detail: 'Yerel istemciler veya tarayıcı, tam oturum kaydı',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Kubernetes ve OpenShift erişimi',
        detail: 'Yerel kubectl / oc, fiil ve namespace politikası, JIT token’lar',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Yerel araçlarla veritabanı erişimi',
        detail: 'psql, mysql, SSMS ve benzerleri ağ geçidi üzerinden',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'SQL komut politikası ve dinamik veri maskeleme',
        detail: 'İfade düzeyinde kontrol, maskelenmiş sonuç kümeleri',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Tarayıcı tabanlı RDP / VNC / Telnet',
        detail: 'Birinci sınıf Windows ve cihaz erişimi',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Rotasyonlu kimlik bilgisi kasası',
        detail: 'Var olmak zorunda olan hesaplar için',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Oturum videosu + tuş vuruşu dökümü',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Onaylı tam zamanında erişim',
        detail: 'Süre sınırlı, onaya bağlı yetkilendirmeler',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'IAM + IGA aynı platformda',
        detail: 'SSO, MFA, yaşam döngüsü, erişim gözden geçirmeleri',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Kendi altyapınızda veya bulutta',
        detail: 'Aynı ürün, aynı fiyat',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Eşzamanlı oturum başına fiyatlandırma',
        detail: 'Kaynak veya kullanıcı başına değil',
        monofor: true,
        competitor: 'Teklife dayalı',
      },
      {
        feature: 'Her hedefe ajan kurmadan dakikalar içinde kurulum',
        monofor: true,
        competitor: 'partial',
      },
    ],
    faqs: [
      {
        q: 'Monopam, Kubernetes’i Teleport gibi kapsıyor mu?',
        a: 'Evet. Monopam; Kubernetes ve OpenShift erişimini kısa ömürlü kubeconfig’ler ve tam zamanında TokenRequest token’larıyla aracılar, fiil ve namespace düzeyinde politika uygular, exec oturumlarını kaydeder ve mühendislerin yerel kubectl ile oc kullanmaya devam etmesine izin verir.',
      },
      {
        q: 'Windows / RDP desteği ne kadar derin?',
        a: 'Birinci sınıf. RDP, istemci kurulumu olmadan tarayıcıda çalışır; oturumlar video artı tuş vuruşu dökümü olarak kaydedilir ve kasadaki kimlik bilgileri otomatik enjekte edildiği için yöneticiler parolayı hiç görmez. VNC ve Telnet ise hiç modernleşmeyen cihazları kapsar.',
      },
      {
        q: 'Mühendisler araçlarından vazgeçmek zorunda mı?',
        a: 'Hayır. kubectl, oc, psql, mysql ve yerel SSH istemcileri; politika ve kayıt yol üzerinde uygulanarak Monopam ağ geçidi üzerinden çalışır. Tarayıcı konsolu bir seçenektir, zorunluluk değil.',
      },
      {
        q: 'Kısa ömürlü sertifikalar gelecekse, kimlik bilgilerini neden hâlâ kasada saklayalım?',
        a: 'Çünkü birçok ayrıcalıklı hesap her koşulda var olmak zorunda: root ve yerel admin, veritabanı sa, servis hesapları ve sertifika doğrulamayı asla desteklemeyecek cihaz girişleri. Bunlar kasa, rotasyon ve teslim alma kontrolü ister: yalnızca sertifikaya dayalı modellerin size bıraktığı PAM parçası.',
      },
      {
        q: 'Kendi altyapımızda çalıştırabilir miyiz?',
        a: 'Evet. Monopam kendi altyapınızda self-hosted veya SaaS olarak kurulur (aynı ürün, aynı fiyat) ve kaynak başına değil eşzamanlı oturum başına fiyatlandırılır; filonuz büyüdükçe faturanız büyümez.',
      },
    ],
  },
}

type Props = { locale: string }

export default function TeleportAlternativePage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <ComparePage
      leadMagnet={
        <AssetDownload
          asset="pam-rfp"
          title={{
            en: 'Evaluating PAM vendors? Take the 50 questions.',
            tr: 'PAM tedarikçisi mi değerlendiriyorsunuz? 50 soruyu alın.',
          }}
          copy={{
            en: 'A vendor-neutral RFP template: 50 questions across vault, sessions, JIT, Kubernetes, databases, certificates, EPM, architecture, integrations, and commercials, with a scoring rubric and the red flags to watch for.',
            tr: 'Tedarikçi-bağımsız RFP şablonu: kasa, oturumlar, JIT, Kubernetes, veritabanları, sertifikalar, EPM, mimari, entegrasyonlar ve ticari koşullarda 50 soru; puanlama cetveli ve dikkat edilecek kırmızı bayraklarla.',
          }}
        />
      }
      competitor="Teleport"
      locale={locale}
      bilingual
      path="/compare/teleport-alternative"
      seoTitle={c.seoTitle}
      seoDescription={c.seoDescription}
      heroEyebrow={c.heroEyebrow}
      heroTitle={
        <>
          {c.heroTitle[0]}
          <br />
          {c.heroTitle[1]}
        </>
      }
      heroBody={c.heroBody}
      reasons={c.reasons}
      tableTitle={
        <>
          {c.tableTitle[0]}
          <br />
          {c.tableTitle[1]}
        </>
      }
      tableBody={c.tableBody}
      rows={c.rows}
      faqs={c.faqs}
    />
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
